import React, { Component } from 'react';
import FaqSection from './FaqSection';
import './pentities.css';
import './HRHub.css';
import NavBar from './NavBar';

interface HRHubProps {}

interface HRHubState {}

class HRHub extends Component<HRHubProps, HRHubState> {
  render() {
    return (
      <div className="pentities-page hrhub-v2-page">
        {/* Top Navigation */}
        <NavBar />

        {/* Hero Section */}
        <section className="pentities-hero-section">
          <div className="pentities-hero-video-background">
            <video preload="metadata" autoPlay loop muted playsInline className="pentities-hero-video">
              <source src={require('./assets/video/web/14380225_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="pentities-container">
            <div className="pentities-hero-content">
              <span className="pentities-hero-eyebrow">Employee Hub &amp; HR Self-Service</span>
              <h1 className="pentities-hero-title">
                The Employee Hub HR self-service inside Microsoft Teams
              </h1>
              <p className="pentities-hero-description">
                Everything an employee, a manager and HR need profile, leave, announcements, approvals, surveys,
                tasks and tickets inside the Microsoft Teams window they already use all day. Running entirely
                inside your own Microsoft 365 and Azure tenant, so no employee data ever leaves your control.
              </p>
              <div className="pentities-hero-cta">
                <a
                  href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pentities-hero-cta-btn"
                >
                  Request Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* General Description / Executive summary */}
        <section id="overview" className="pentities-gendesc-section">
          <div className="pentities-container">
            <div className="pentities-gendesc-content">
              <span className="pentities-gendesc-label">Executive Summary</span>
              <h2 className="pentities-gendesc-title">Self-service that people actually use because it is where they already work</h2>
              <p>
                Most HR software asks people to leave the tools they work in, log in somewhere else, and learn a new
                place to book leave, read an announcement, chase an approval or check who is off. That friction is why
                self-service adoption stalls and why HR spends its days answering questions a portal was supposed to
                answer.
              </p>
              <p>
                pEntities takes the opposite approach. The Employee Hub lives inside Microsoft Teams, beside the
                conversations and the work, and gives every person exactly the surface their role needs: an
                <b> employee</b> sees their profile, leave and announcements; a <b>manager</b> sees their team,
                approvals and workload; <b>HR</b> broadcasts, surveys and governs.
              </p>
              <p>
                Everything is live in the product today a single self-service portal in seven languages, working-day
                leave management with a two-stage approval chain, a multilingual company-communications inbox with
                ratings and video, live Teams presence and calendar, engagement surveys, a Planner-backed task manager,
                and an embedded helpdesk all on a per-tenant, Entra-secured backend.
              </p>
              <p>
                <b>Value for the organisation.</b> Fewer HR tickets, faster approvals, and every employee record held
                inside your own Microsoft 365 tenant, governed by the security you already run.
              </p>
            </div>
          </div>
        </section>

        {/* Part 1 The Employee Hub (dark) */}
        <section id="employee-hub" className="pentities-docmgmt-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">Part 1 The Employee Hub</span>
            <h2 className="pentities-docmgmt-title">One personal page inside Teams for every employee, every manager, and HR</h2>
            <p className="pentities-docmgmt-lead">
              No separate portal, no extra login, no new place to check. The hub is one clean, personal page inside
              Microsoft Teams, in the user's own language, on any screen, following their light or dark theme.
            </p>

            <div className="pentities-docmgmt-block">
              <h3>1 · One portal, right inside Teams</h3>
              <p>
                A single-page hub in seven languages, with everything stacked as collapsible sections opened or tucked
                away in one click.
              </p>
              <ul>
                <li><b>Everything in one scroll</b> profile, leave, team, announcements, surveys, tasks and tickets in one place.</li>
                <li><b>In their language</b> English, Greek, German, French, Japanese, Bulgarian and Romanian, so a multinational workforce reads it natively.</li>
                <li><b>On any screen</b> the layout reflows from a wide desktop down to the Teams mobile app.</li>
                <li><b>Light or dark</b> the hub follows the user's Teams theme automatically.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>2 · My profile &amp; my manager</h3>
              <p>Every employee opens the hub to a clear picture of who they are and who they report to.</p>
              <ul>
                <li><b>My profile</b> name, photo, job title, department, status, hire date and country, drawn from the HR record and Microsoft Entra.</li>
                <li><b>My manager</b> a card for the person they report to, with a live Teams presence badge.</li>
                <li><b>Reach them in one click</b> start a Teams chat, place a call, or schedule a 1:1 straight from the card.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>3 · Leave &amp; absence, done properly</h3>
              <p>Booking time off is the thing employees do most so pEntities makes it fast and correct.</p>
              <ul>
                <li><b>Balances at a glance</b> total annual leave, days used, days remaining and pending requests, always current.</li>
                <li><b>Request in seconds</b> the hub calculates exact working days, excluding weekends and the employee's country public holidays.</li>
                <li><b>Every request tracked</b> a filterable list with colour-coded status, type, dates, duration and approver.</li>
                <li><b>A real calendar</b> a monthly leave calendar overlays the employee's Microsoft 365 meetings and group events.</li>
                <li><b>Many leave types</b> vacation, sick, unpaid, parental, bereavement, study, compensation and public-duty, configurable per organisation.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>4 · For managers: the team, and the approvals</h3>
              <p>Managers get a purpose-built layer on top of the same hub.</p>
              <ul>
                <li><b>My team</b> a card per direct report with live presence, working status, department, contact and remaining leave.</li>
                <li><b>Approvals inbox</b> a badge counts pending requests; approve, hold or reject in a click, written straight back to the employee.</li>
                <li><b>Two-stage approval</b> where policy requires it, a manager's approval automatically forwards to HR for a second sign-off.</li>
                <li><b>Act as a team</b> message, call or book a 1:1, assign a task, or multi-select the team and schedule a group Teams meeting.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>5 · Company communications that land</h3>
              <p>HR needs to reach people and know the message landed. The communications inbox is built for exactly that.</p>
              <ul>
                <li><b>An announcements inbox</b> scheduled HR notifications at the top of every hub, colour-coded by priority, with unread badges.</li>
                <li><b>Rich messages</b> full formatted messages with inline video, served securely from the organisation's own storage.</li>
                <li><b>Read and rated</b> employees mark a message read and give it a 1–5 star rating, so HR sees reach and reception.</li>
                <li><b>Read in any language</b> switch an individual message between the seven supported languages with a click.</li>
                <li><b>Surveys &amp; engagement</b> active engagement surveys surface in the hub, with an anonymous option, completed without leaving Teams.</li>
              </ul>
            </div>

            <div className="pentities-docmgmt-block">
              <h3>6 · Tasks, training and tickets</h3>
              <p>The hub closes the loop on the day-to-day work an employee is accountable for.</p>
              <ul>
                <li><b>My tasks</b> a full personal task manager backed by Microsoft Planner; filter, tick off checklist items, set due dates and complete in place. Managers assign tasks the same way.</li>
                <li><b>Never miss mandatory training</b> an alert banner lists overdue training, with a one-click "Notify Manager" escalation.</li>
                <li><b>Raise a ticket without leaving</b> an embedded helpdesk lets employees raise and track IT/HR tickets in the same window.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Part 2 The engine that runs it (white) */}
        <section id="engine" className="pentities-workflow-section">
          <div className="pentities-container">
            <span className="pentities-workflow-eyebrow">Part 2 The engine that runs it</span>
            <h2 className="pentities-workflow-title">Your data, in your tenant wired natively into Microsoft 365</h2>
            <p className="pentities-workflow-lead">
              The Employee Hub runs on your own Azure subscription and is wired into Microsoft Entra, Graph and
              Planner, so it reflects the organisation as it really is with nothing to migrate and no separate
              directory to maintain.
            </p>

            <div className="pentities-workflow-block">
              <h3>7 · Your data, in your tenant</h3>
              <p>The hub runs on the organisation's own Azure subscription, and every byte of HR data stays there.</p>
              <ul>
                <li><b>One container per tenant</b> each customer's HR data lives in its own physically separate Azure Blob container, not a shared store with a filter column.</li>
                <li><b>A record per employee</b> a master roster plus a private record per person, addressed by the employee's email.</li>
                <li><b>Media kept private</b> announcement videos and attachments are served through short-lived, read-only links, never public URLs.</li>
              </ul>
            </div>

            <div className="pentities-workflow-block">
              <h3>8 · Microsoft Entra &amp; Graph, built in</h3>
              <p>The hub is wired into Microsoft 365, so it reflects the organisation as it really is.</p>
              <ul>
                <li><b>Directory sync</b> employees are imported from Microsoft Entra ID with an additive, non-destructive sync that never overwrites existing records.</li>
                <li><b>Real reporting lines</b> the manager hierarchy is read directly from Entra, so teams and approval routing match the org chart.</li>
                <li><b>Live presence &amp; calendar</b> manager and team presence and the employee's own meetings come straight from Microsoft Graph.</li>
                <li><b>Acts as the user</b> delegated permissions mean the hub can never see data that person could not see themselves.</li>
              </ul>
            </div>

            <div className="pentities-workflow-block">
              <h3>9 · Notifications that reach people</h3>
              <p>When HR needs to tell people something, the hub delivers it through every channel at once.</p>
              <ul>
                <li><b>Three channels</b> every announcement can go out as a Teams message, an email, and an in-app inbox entry.</li>
                <li><b>Audience targeting</b> send to the whole company, a specific department, or named individuals.</li>
                <li><b>Scheduled or immediate</b> messages fire now or queue for a future date, released automatically by the scheduler.</li>
              </ul>
            </div>

            <div className="pentities-workflow-block">
              <h3>10 · Automation on HR events</h3>
              <p>HR moments can start automation without anyone lifting a finger, through the pEntities workflow engine.</p>
              <ul>
                <li><b>HR triggers</b> workflows fire on leave submitted, approved or rejected, training expiring, a work anniversary, or a contract ending.</li>
                <li><b>De-duplicated and reliable</b> each event fires a workflow at most once per day, on the durable execution engine that powers the platform.</li>
                <li><b>Do anything downstream</b> a triggered workflow can notify, create tasks, send documents or invoke AI assembled with no code.</li>
              </ul>
            </div>

            <div className="pentities-workflow-block">
              <h3>11 · Security &amp; access control</h3>
              <p>Who sees what is governed at every step.</p>
              <ul>
                <li><b>Roles that matter</b> HR Admin and Manager roles gate the privileged surfaces; every employee gets self-service to their own record.</li>
                <li><b>Isolated by tenant</b> every request is bound to the caller's tenant, and data is reached only in that tenant's container.</li>
                <li><b>Least privilege by construction</b> the hub acts as the signed-in user, so access is always bounded by what that person is already entitled to see.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How it compares (dark) */}
        <section id="compare" className="pentities-docmgmt-section hrhub-compare-section">
          <div className="pentities-container">
            <span className="pentities-docmgmt-eyebrow">How pEntities compares</span>
            <h2 className="pentities-docmgmt-title">A capability inside Microsoft 365 not a separate destination</h2>
            <p className="pentities-docmgmt-lead">
              A general HR suite is a separate website employees must be sent to. The Employee Hub is a capability
              inside the Microsoft 365 they already live in.
            </p>
            <div className="hrhub-compare-table">
              <div className="hrhub-compare-row hrhub-compare-head">
                <span>Consideration</span>
                <span>pEntities Employee Hub</span>
                <span>A separate HR portal</span>
              </div>
              <div className="hrhub-compare-row">
                <span>Where people work</span>
                <span>Inside Microsoft Teams, beside the conversation</span>
                <span>A separate website and login</span>
              </div>
              <div className="hrhub-compare-row">
                <span>Directory &amp; org chart</span>
                <span>Live from Microsoft Entra &amp; Graph</span>
                <span>Imported and re-keyed</span>
              </div>
              <div className="hrhub-compare-row">
                <span>Presence &amp; calendar</span>
                <span>Live from Microsoft 365</span>
                <span>Not available, or bolted on</span>
              </div>
              <div className="hrhub-compare-row">
                <span>Leave calculation</span>
                <span>Working days, local public holidays, live balances</span>
                <span>Often manual or generic</span>
              </div>
              <div className="hrhub-compare-row">
                <span>Languages</span>
                <span>7, switchable per user and per message</span>
                <span>Usually one, set by admin</span>
              </div>
              <div className="hrhub-compare-row">
                <span>Where data lives</span>
                <span>Your own Azure tenant, isolated per customer</span>
                <span>A shared multi-tenant HR cloud</span>
              </div>
              <div className="hrhub-compare-row">
                <span>Automation</span>
                <span>No-code workflows on HR events</span>
                <span>Fixed features, or none</span>
              </div>
            </div>
          </div>
        </section>

        {/* Modules Section */}
        <section id="modules" className="pentities-modules-section">
          <div className="pentities-container">
            <span className="pentities-modules-eyebrow">pEntities Modules</span>
            <h2 className="pentities-modules-title">Purpose-built modules for the work your teams actually do</h2>
            <p className="pentities-modules-lead">
              One platform, four specialised modules each with its own domain AI agent, all running inside
              Microsoft Teams on the data you already own.
            </p>

            <div className="pentities-modules-grid">
              <a href="/solutions/hr-template" className="pentities-module-card">
                <h3>HR Module</h3>
                <p>
                  The full employee lifecycle onboarding with automatic task lists, leave with multi-level
                  approval, performance reviews, training tracked to its expiry date and payroll analysis. The HR
                  agent spots compliance risks and writes the reports for you.
                </p>
                <span className="pentities-module-link">Explore HR →</span>
              </a>

              <a href="/solutions/case-management" className="pentities-module-card">
                <h3>Legal Module</h3>
                <p>
                  Every case tracked from pre-trial to appeal court, lawyers, opponents, confidentiality level,
                  financial exposure and risk. Contracts carry milestones, auto-renewal detection and signature
                  history, while the agent analyses timelines and exposure scenarios.
                </p>
                <span className="pentities-module-link">Explore Legal →</span>
              </a>

              <a href="/solutions/ticketing" className="pentities-module-card">
                <h3>Ticketing Module</h3>
                <p>
                  Every request handled with precision IT, HR, finance or legal. Real-time tracking, automatic
                  notifications, approval flows and full history, with priorities, SLAs and automation rules that
                  work without anyone intervening.
                </p>
                <span className="pentities-module-link">Explore Ticketing →</span>
              </a>

              <a href="/solutions/finance" className="pentities-module-card">
                <h3>Finance Module</h3>
                <p>
                  Invoices, expenses, purchases, budgets, receivables and tax data organised by fiscal year. Every
                  figure links back to its original email, document and approval task full traceability, and an
                  agent that spots trends and deviations.
                </p>
                <span className="pentities-module-link">Explore Finance →</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <FaqSection path="/solutions/hr-hub" />
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default HRHub;
